// Created a special API file for global RBAC permissions

import axiosInstance from "@/lib/axiosInstance";
import type { CustomAxiosRequestConfig } from "@/types/common.types";
import type { QueryClient } from "@tanstack/react-query";
import type { SwitchRef } from "@/api/access/access.types";
import {
  requiredModuleByPath,
  requiredSwitchByPath,
  type ModuleKey,
  type ModulePermissionsDetail,
  type PermissionsResponse,
} from "./permissions.types";

export interface RouterContextType {
  queryClient: QueryClient;
}

const permissionsEndpoint = "/user-management/user-role/permissions" as const;

export const PermissionsQueryKeys = {
  root: "permissions",
  module: "module",
} as const;

// SERV-3232: the answer is the role held in the scope the request goes out in, so the scope is
// in the key and is sent with the call; a scope switch can then never file one entity's
// permissions under another's key.
export const permissionsQueryKey = (entityTenant: string) =>
  [PermissionsQueryKeys.root, entityTenant] as const;

export async function permissionsQueryFn(entityTenant: string): Promise<PermissionsResponse> {
  const { data } = await axiosInstance.get(permissionsEndpoint, {
    actingEntityId: entityTenant,
  } as CustomAxiosRequestConfig);
  return data;
}

export const permissionsQueryOptions = (entityTenant: string) => ({
  queryKey: permissionsQueryKey(entityTenant),
  queryFn: () => permissionsQueryFn(entityTenant),
  staleTime: Infinity, // prevent refetching permissions on every page load
});

const modulePermissionsEndpoint = (module: ModuleKey) =>
  `/user-management/user-role/permissions/${module}` as const;

export async function modulePermissionsQueryFn(
  entityTenant: string,
  module: ModuleKey,
): Promise<ModulePermissionsDetail> {
  const { data } = await axiosInstance.get(modulePermissionsEndpoint(module), {
    actingEntityId: entityTenant,
  } as CustomAxiosRequestConfig);
  return data;
}

export const modulePermissionsQueryOptions = (entityTenant: string, module: ModuleKey) => ({
  queryKey: [PermissionsQueryKeys.root, PermissionsQueryKeys.module, entityTenant, module] as const,
  queryFn: () => modulePermissionsQueryFn(entityTenant, module),
  staleTime: Infinity,
});

function resolveFromPath<T>(map: Record<string, T>, pathname: string): T | null {
  // Trailing slashes are not normalized upstream, and a nested exact key such as
  // "/service-providers/w9-requests" would otherwise miss and fall through to the
  // base segment, gating the path on the parent module instead of its own.
  const trimmed = pathname.replace(/\/+$/, "");
  const normalizedPath = trimmed === "" ? "/" : trimmed;

  if (Object.hasOwn(map, normalizedPath)) return map[normalizedPath];

  // base segment match: "/roles-permissions/add-role" -> "/roles-permissions"
  const segments = normalizedPath.split("/").filter(Boolean);
  if (segments.length === 0) return null;

  const basePath = `/${segments[0]}`;
  return Object.hasOwn(map, basePath) ? map[basePath] : null;
}

export function resolveModuleFromPath(pathname: string): ModuleKey | null {
  return resolveFromPath(requiredModuleByPath, pathname);
}

export function resolveSwitchFromPath(pathname: string): SwitchRef | null {
  return resolveFromPath(requiredSwitchByPath, pathname);
}
